/* eslint-disable max-len */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import * as RouterDom from "react-router-dom";
import { toHex } from "web3-utils";

import {
  Box,
  CircularProgress,
  // Stack,
  createSvgIcon,
  Grid,
  IconButton,
  Button as MuiButton,
  styled,
  SvgIcon,
  Tooltip,
  Zoom,
  Link as MuiLink,
} from "@mui/material";

import MetaMaskIcon from "../../../assets/metaMask.svg";
import ApiRoutes from "../../../config/api.routes";
import useMetaMask from "../../../contexts/useMetaMask";
import AuthService, { fetchOrganization } from "../../../services/auth.service";
import { useAxios } from "../../../services/http.service";
// import WalletConnectIcon from "../../../assets/walletConnect.svg";
// import CoinBaseIcon from "../../../assets/coinBase.svg";
import { VerifiedUserRounded as Person, HealthAndSafety as Shield, WalletOutlined } from "@mui/icons-material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { AppRoutes } from "../../../config";
import useAuthorization from "../../../contexts/useAuthorization";
import { MainChainID } from "../../../utils/constants";
import { consoleLog, toReadable } from "../../../utils/methods";
// import ArrowBackIcon from '@mui/icons-material/ArrowBack';
// import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Text } from "../../../components/Typography";
import { getReadOnlyContract } from "../../../services/contract.service";
import PopupDialog from "../../PopupDialog";
import {
  CircularIcon,
  ConnectBody,
  DialogInfo,
  IconContainer,
  SignInBody,
  SignInSheet,
  SignInTitle,
} from "../dialogStyles";
import { PopupActionButton, PopupGhostButton, PopupProcessingButton } from "../../PopupButtons";

type UserAccountProps = {
  closeModal?(): void;
  accountsChangedMessage?(val: any): void;
};

export const UserAccount = ({ closeModal, accountsChangedMessage }: UserAccountProps) => {
  const axios = useAxios();
  const navigate = useNavigate();
  const { palette } = useTheme();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md")); // md corresponds to 960px by default

  const { loggedIn, setUser, setLogged, user, isGuardian, isPioneer, isAdmin, isGlobalTrustAdmin, isTrust } =
    useAuthorization();
  const {
    connect,
    connectAndSign,
    signer,
    disconnect,
    account,
    currentAccount,
    chainId,
    web3Provider,
    isAppSwitchWallet,
  } = useMetaMask();

  const [walletDialogOpen, setWalletDialogOpen] = useState(false);
  const [wrongChainDialogOpen, setWrongChainDialogOpen] = useState(false);
  const [signMessageDialogOpen, setSignMessageDialogOpen] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);
  const [signatureLoading, setSigning] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [balance, setBalance] = useState<any>();
  // const [currentSlide, setCurrentSlide] = useState(0);
  // const currentSlide = 0;
  // const handlePrevSlide = () => {
  //   setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  // };

  // const handleNextSlide = () => {
  //   setCurrentSlide((prev) => (prev + 1) % slides.length);
  // };

  // const slides = [
  //   {
  //     title: 'EcoFutures',
  //     description: "EcoFutures is a portal bringing Earth's natural environments on-chain as TREEs (EFT) asset-backed NFTs.",
  //     image: { Waterfall }  // Replace with actual image path
  //   }
  //   // Add more slides as needed
  // ];

  // Check for #login hash on mount
  useEffect(() => {
    if (window.location.hash === "#login" && !loggedIn) {
      setWalletDialogOpen(true);
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for hash changes (e.g., returning from MetaMask app with #login)
  useEffect(() => {
    const handleHashChange = () => {
      if (window.location.hash === "#login" && !loggedIn) {
        setWalletDialogOpen(true);
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
      }
    };
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [loggedIn]);

  useEffect(() => {
    const handleAccountsChanged = async (accounts: any) => {
      const isToken = AuthService.loggedIn();
      const isUser = !!user;

      if (accountsChangedMessage) {
        try {
          accountsChangedMessage(`Changed to ${accounts[0]}`);
          setTimeout(() => {
            accountsChangedMessage(undefined);
          }, 10000);
        } catch {
          accountsChangedMessage("Changing accounts error");
          setTimeout(() => {
            accountsChangedMessage(undefined);
          }, 10000);
        }
      }

      consoleLog("Handle accounts changed", { accounts });

      if (accounts.length) {
        const nextAccount = accounts[0];

        if (isUser) {
          if (user.address) {
            if (user.address !== nextAccount) {
              AuthService.logout();
              navigate(AppRoutes.Home.path);
              if (closeModal) {
                closeModal();
              }
              consoleLog("Set next current account", nextAccount);
              setSignMessageDialogOpen(true);
            }
          } else {
            // allow app to fetch token from storage and authorize user if ok
          }
        } else {
          consoleLog(`Handle accounts changed. No user. ${isToken ? "Has" : "No"} token`);
          if (isToken) {
            const authResponse = await AuthService.checkAuth();
            if (authResponse) {
              setUser(authResponse);
            } else {
              setUser(null);
              setSignMessageDialogOpen(false);
              AuthService.logout();
              if (closeModal) {
                closeModal();
              }
              navigate(AppRoutes.Home.path);
            }
          }
        }
      } else {
        consoleLog("Handle accounts changed. No accounts found in Metamask");
        if (isToken || isUser) {
          consoleLog(`Handle accounts changed. ${isUser ? "Has" : "No"} user. ${isToken ? "Has" : "No"} token`);
          handleLogout();
          setSignMessageDialogOpen(false);
          setSigning(false);
          setWalletDialogOpen(false);
        }
      }
    };

    const handleChainChanged = (chainID: string) => {
      if (
        chainID !== toHex(MainChainID) &&
        (loggedIn || walletDialogOpen || signMessageDialogOpen || connectLoading || signatureLoading)
      ) {
        setSignMessageDialogOpen(false);
        setWalletDialogOpen(false);
        setWrongChainDialogOpen(true);
      } else if (chainID === toHex(MainChainID) && wrongChainDialogOpen) {
        // User switched to the correct chain — dismiss the wrong-chain dialog
        setWrongChainDialogOpen(false);
      }
    };

    if (web3Provider) {
      web3Provider.on("accountsChanged", handleAccountsChanged);
      web3Provider.on("chainChanged", handleChainChanged);
    }

    return () => {
      if (web3Provider) {
        web3Provider.removeListener("accountsChanged", handleAccountsChanged);
        web3Provider.removeListener("chainChanged", handleChainChanged);
      }
    };
  }, [web3Provider, user]);

  useEffect(() => {
    if (!error) {
      setErrorMessage("");
    }
  }, [error]);

  useEffect(() => {
    if (!errorMessage) {
      setError(false);
    } else {
      setError(true);
    }
  }, [errorMessage]);

  useEffect(() => {
    // Listen for the custom event to open the wallet dialog
    const openWalletDialogListener = () => setWalletDialogOpen(true);
    window.addEventListener("openWalletDialog", openWalletDialogListener);
    return () => {
      window.removeEventListener("openWalletDialog", openWalletDialogListener);
    };
  }, []);

  const handleButtonClick = () => {
    if (loggedIn) {
      navigate(AppRoutes.Profile.subRoutes.Dashboard.path);
    } else {
      setWalletDialogOpen(true);
      setError(false);
    }
  };

  const handleDisconnect = () => {
    consoleLog("Disconnecting");
    // handleLogout already calls disconnect(), so we don't call it separately here
    handleLogout();

    setSigning(false);
    setSignMessageDialogOpen(false);
    setWrongChainDialogOpen(false);
    setWalletDialogOpen(false);
    setTimeout(() => setError(false), 1000);
  };

  // The backend challenge is a fixed prefix; the signed message is prefix + timestamp, accepted
  // for signatureConfig.timestampGap after that timestamp (eco-rest-api validateSignature guard).
  // Cache the prefix so a tap can build the message synchronously: on mobile the wallet call has
  // to follow the tap with no network round-trip in between, or the browser won't open MetaMask.
  const authPrefixRef = useRef<string | null>(null);
  const authPrefixRequestRef = useRef<Promise<string> | null>(null);
  const loadAuthPrefix = (): Promise<string> => {
    if (authPrefixRef.current) {
      return Promise.resolve(authPrefixRef.current);
    }
    if (!authPrefixRequestRef.current) {
      authPrefixRequestRef.current = axios
        .get(ApiRoutes.user.authString, { withCredentials: false })
        .then((res) => res)
        .catch((err) => err.response)
        .then((response) => {
          if (!response || !response.data || response.status !== 200) {
            throw new Error("Failed to fetch authentication challenge. Please try again.");
          }
          authPrefixRef.current = response.data as string;
          return authPrefixRef.current;
        })
        .finally(() => {
          authPrefixRequestRef.current = null;
        });
    }
    return authPrefixRequestRef.current;
  };

  // Warm the prefix as soon as either login dialog opens.
  useEffect(() => {
    if (walletDialogOpen || signMessageDialogOpen) {
      loadAuthPrefix().catch(() => undefined);
    }
  }, [walletDialogOpen, signMessageDialogOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Login, step 1. Desktop / MetaMask in-app browser: connect + sign + authorize in one go — the
  // extension pops up twice with no app switching. Mobile browser (Connect SDK): connect only,
  // then hand over to the "Authenticate to continue" dialog (handleSignMessage). Every approval
  // there is a trip to the MetaMask app, and each trip needs its own tap to open the app; a
  // separate screen also shows the user the connect worked before asking them to sign.
  const handleWalletClick = async () => {
    setConnectLoading(true);
    setErrorMessage("");

    try {
      if (isAppSwitchWallet) {
        // Already connected (e.g. dismissed the sign step earlier): skip straight to step 2
        // rather than sending the user to MetaMask for a connect that's already in place.
        const connected = account ? true : await connect();
        if (connected !== true) {
          setErrorMessage(
            typeof connected === "string" ? connected : "Failed to connect the wallet. Please try again."
          );
          return;
        }
        setWalletDialogOpen(false);
        setSignMessageDialogOpen(true);
        return;
      }

      // dataTs is built only once connected, so its timestamp is fresh and the prefix fetch
      // (usually cached already) overlaps the connect.
      let dataTs = "";
      const result = await connectAndSign(async () => (dataTs = (await loadAuthPrefix()) + Date.now()));

      // Block authorization if the wallet ended up on the wrong chain — either the SDK
      // signed on the wrong network or the user rejected/can't perform the chain switch.
      const currentChainId = await web3Provider?.request({ method: "eth_chainId" }).catch(() => null);
      if (currentChainId && currentChainId !== toHex(MainChainID)) {
        setWalletDialogOpen(false);
        setWrongChainDialogOpen(true);
        return;
      }

      if (typeof result === "string") {
        setErrorMessage(result);
        return;
      }

      // Step 3: authorize with the backend using the signature
      const { accounts, signature } = result;
      const authResponse = await AuthService.authorize(accounts[0], dataTs, signature);

      if (!authResponse || !authResponse.token) {
        setErrorMessage("Failed to perform authorization. Please try again later!");
        return;
      }

      const { account: authAccount, token } = authResponse;
      AuthService.setToken(token);

      if (authAccount.role === "trust") {
        const org = await fetchOrganization();
        setUser(org ? { ...authAccount, organization: org } : authAccount);
      } else {
        setUser(authAccount);
      }

      setLogged(true);
      setWalletDialogOpen(false);
      setSignMessageDialogOpen(false);
    } catch (e: any) {
      console.log({ e });
      if (e?.code === 4001) {
        setErrorMessage("MetaMask request denied. Please try again.");
      } else {
        setErrorMessage("Failed to connect the wallet. Please try again.");
      }
    } finally {
      setConnectLoading(false);
    }
  };

  // Login step 2 on mobile browsers, and the re-auth flow (e.g. after the user switches accounts
  // in MetaMask). With a connected app-switch wallet this is a bare personal_sign built
  // synchronously from the cached prefix, so the tap's deep link opens MetaMask straight on the
  // signature request. Otherwise connect + sign as in handleWalletClick.
  const handleSignMessage = async () => {
    setSigning(true);
    setError(false);
    try {
      let dataTs = "";
      let result: Awaited<ReturnType<typeof connectAndSign>>;
      if (isAppSwitchWallet && account) {
        dataTs = (authPrefixRef.current ?? (await loadAuthPrefix())) + Date.now();
        const signature = await signer(dataTs);
        result = { accounts: [account], chainId: chainId ?? "", signature };
      } else {
        result = await connectAndSign(async () => (dataTs = (await loadAuthPrefix()) + Date.now()));
      }

      const currentChainId = await web3Provider?.request({ method: "eth_chainId" }).catch(() => null);
      if (currentChainId && currentChainId !== toHex(MainChainID)) {
        setSignMessageDialogOpen(false);
        setWrongChainDialogOpen(true);
        setSigning(false);
        return;
      }

      if (typeof result === "string") {
        setErrorMessage(result);
        setSigning(false);
        return;
      }

      const { accounts, signature } = result;
      const authResponse = await AuthService.authorize(
        (currentAccount as string) || accounts[0] || (account as string),
        dataTs,
        signature
      );

      if (authResponse) {
        const { account: authAccount, token } = authResponse;

        if (token) {
          AuthService.setToken(token);

          if (authAccount.role === "trust") {
            const org = await fetchOrganization();
            setUser(org ? { ...authAccount, organization: org } : authAccount);
          } else {
            setUser(authAccount);
          }
          setLogged(true);
          setSigning(false);
          setSignMessageDialogOpen(false);
        } else {
          setSigning(false);
          setErrorMessage("Failed to perform authorization. No token in response. Please try again later!");
        }
      } else {
        setSigning(false);
        setErrorMessage("Failed to perform authorization. Please try again later!");
      }
    } catch (e: any) {
      console.log({ error: e });
      if (e.code === 4001) {
        setErrorMessage("MetaMask Signature failed. User denied message signature.");
      } else {
        setErrorMessage("Failed to perform authorization. Please try again later!");
      }
      setSigning(false);
    }
  };

  const handleLogout = () => {
    disconnect();
    setLogged(false);
    if (closeModal) {
      closeModal();
    }
    AuthService.logout();
    navigate(AppRoutes.Home.path);
  };

  // const isVerified = user?.emailVerified && user?.referrerAddress;
  const emailVerified = user?.emailVerified;

  const fetchBalance = async () => {
    setBalance(
      <Text numeric medium>
        Loading...
      </Text>
    );

    try {
      const ercContract = getReadOnlyContract("erc20");

      if (ercContract && user && user.address) {
        try {
          // Get the balance for the user's address
          const ercBalance = await ercContract.balanceOf(user.address);

          setBalance(
            <MuiLink
              href="https://portfolio.metamask.io"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                alignItems: "flex-start",
                display: "flex",
                color: "white",
                textDecoration: "none",
                "&:hover": {
                  color: "white",
                },
              }}
            >
              <Text
                numeric
                medium
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <WalletOutlined sx={{ mr: 3, display: "flex", alignSelf: "center" }} />
                {toReadable(ercBalance, true)}
                <Text
                  sx={{
                    ml: 3,
                    display: "flex",
                    alignItems: "center",
                  }}
                  medium
                >
                  USD
                </Text>
              </Text>
            </MuiLink>
          );
        } catch (error) {
          console.error("Error fetching balance:", error);
          setBalance(
            <Text numeric medium>
              Cannot fetch balance
            </Text>
          );
        }
      } else {
        setBalance(
          <Text numeric medium>
            Cannot fetch balance
          </Text>
        );
      }
    } catch (error) {
      console.error("Error getting MetaMask signer:", error);
      setBalance(
        <Text numeric medium>
          Cannot fetch balance
        </Text>
      );
    }
  };

  useEffect(() => {
    if (loggedIn) {
      fetchBalance();
    }
  }, [loggedIn, user]);

  return (
    <Wrapper>
      {!loggedIn && (
        <>
          {!isSmallScreen ? (
            <Button
              disableRipple
              variant="text"
              size="medium"
              onClick={() => {
                handleButtonClick();
              }}
              sx={{
                width: "auto",
                p: 0,
                mb: 0,
                margin: "0 auto",
                color: isSmallScreen ? "white" : "white",
                textDecoration: "none",
                fontSize: { xs: "26px", md: "16px" },
              }}
            >
              Connect
            </Button>
          ) : (
            <Button
              disableRipple
              variant="text"
              size="small"
              onClick={() => {
                handleButtonClick();
              }}
              sx={{
                border: "none",
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "end",
                padding: "0px 0px",
                color: "white",
                textDecoration: "none",
                fontSize: { xs: "14px", md: "16px" },
                height: "auto",
                fontWeight: "500",
              }}
            >
              {/* <LoginOutlinedIcon sx={{ width: "18px", height: "22px" }} /> */}
              {connectLoading ? "Connecting..." : "Connect"}
            </Button>
          )}
        </>
      )}

      {loggedIn && (
        <>
          {/* {isGuardian && isVerified && !isSmallScreen && (
            <>
              <Link
                sx={{
                  color: { xs: "black", md: "white" },
                }}
                to={AppRoutes.Upload.path}
                onClick={() => closeModal && closeModal()}
              >
                Upload
              </Link>
              <VerticalDivider />
            </>
          )} */}
          {!isSmallScreen && (
            <>
              <Text
                sx={{
                  color: { xs: "black", md: "white" },
                }}
                medium
              >
                {balance}
              </Text>
              <VerticalDivider />
            </>
          )}
          {!isSmallScreen ? (
            <>
              <Link
                to={
                  emailVerified
                    ? AppRoutes.Profile.subRoutes.Dashboard.path
                    : AppRoutes.Profile.subRoutes.Identification.path
                }
              >
                <Tooltip
                  arrow={true}
                  TransitionComponent={Zoom}
                  title={`${
                    isTrust
                      ? "Trustee"
                      : isGlobalTrustAdmin
                      ? "Global Trust Admin"
                      : isAdmin
                      ? "Trust Admin"
                      : isGuardian
                      ? "Guardian"
                      : isPioneer
                      ? "Pioneer"
                      : "Patron"
                  } account`}
                  disableInteractive
                >
                  <ProfileButton
                    sx={{
                      color: "common.black",
                      border: { xs: "none", md: "1px solid" },
                      borderColor: "currentColor",
                    }}
                    size="medium"
                    onClick={() => closeModal && closeModal()}
                  >
                    {isTrust ? (
                      <Shield />
                    ) : isGlobalTrustAdmin ? (
                      <GlobalTrustAdminIcon sx={{ width: "24px", height: "24px" }} />
                    ) : isAdmin ? (
                      <TrustAdminIcon />
                    ) : isGuardian ? (
                      <GuardianIcon sx={{ width: "24px", height: "24px" }} />
                    ) : isPioneer ? (
                      <PioneerIcon sx={{ width: "24px", height: "24px" }} />
                    ) : (
                      <Person />
                    )}
                  </ProfileButton>
                </Tooltip>
              </Link>
              <Tooltip arrow={true} TransitionComponent={Zoom} title="Log out" disableInteractive>
                <LinkLike>
                  <IconButton
                    size="medium"
                    onClick={handleLogout}
                    sx={{
                      backgroundColor: { xs: "transparent", md: "white" },
                      color: { xs: "white", md: "black" },
                      textDecoration: "none",
                      border: { xs: "none", md: "1px solid" },
                      borderColor: "currentColor",
                      "&:hover": {
                        backgroundColor: palette.back.purple, // Adjust the hover background color as needed
                      },
                    }}
                  >
                    <LogoutOutlinedIcon
                      sx={{ width: { xs: "34px", md: "auto" }, height: { xs: "34px", md: "auto" } }}
                    />
                  </IconButton>
                </LinkLike>
              </Tooltip>
            </>
          ) : (
            <Box
              onClick={handleLogout}
              sx={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center" }}
            >
              <LinkLike>
                <IconButton
                  size="medium"
                  onClick={handleLogout}
                  sx={{
                    backgroundColor: { xs: "white", md: "white" },
                    color: { xs: "black", md: "black" },
                    textDecoration: "none",
                    border: { xs: "none", md: "1px solid" },
                    borderColor: "currentColor",
                    "&:hover": {
                      backgroundColor: palette.back.purple, // Adjust the hover background color as needed
                    },
                  }}
                >
                  <LogoutOutlinedIcon sx={{ width: { xs: "18px", md: "auto" }, height: { xs: "18px", md: "auto" } }} />
                </IconButton>
              </LinkLike>
            </Box>
          )}
        </>
      )}

      <DialogInfo
        open={walletDialogOpen}
        // DialogInfo's shared 40% width (it also frames the wrong-network dialog) makes this
        // two-line card sprawl on desktop; hold it near the sign-in card's 420px.
        sx={{ "& .MuiDialog-paper": { maxWidth: "min(440px, 90%)", width: "100%" } }}
        onClose={() => {
          setWalletDialogOpen(false);
          setError(false);
          setSigning(false);
        }}
      >
        <IconContainer>
          <CircularIcon>
            {connectLoading ? (
              <CircularProgress size={32} color="inherit" />
            ) : (
              <InfoOutlinedIcon fontSize="large" color="inherit" />
            )}
          </CircularIcon>
        </IconContainer>
        {/* Same language as the "Authenticate to continue" sheet that follows it: centred title,
            grey subtitle, one pill CTA, a one-line error, a footnote. Stays a centred dialog
            (with the floating badge above) on every screen size. */}
        <ConnectBody>
          <SignInTitle>Connect your wallet</SignInTitle>
          <SignInBody>Connect your MetaMask wallet to use the platform.</SignInBody>
          {error && (
            <SignInError role="alert">
              {errorMessage ? errorMessage : "We are experiencing some technical issues. Please try again later!"}
            </SignInError>
          )}
          <WalletConnectButton onClick={handleWalletClick} disabled={connectLoading} variant="contained" fullWidth>
            {connectLoading ? "Choose account" : "Connect"}
            <WalletIcon src={MetaMaskIcon} alt="" />
          </WalletConnectButton>
          <SignInFootnote>
            Don&apos;t have MetaMask?{" "}
            <a href="https://metamask.io/" target="_blank" rel="noopener noreferrer">
              Download the app
            </a>
            .
          </SignInFootnote>
        </ConnectBody>
      </DialogInfo>

      {/* Login step 2 on phones (after Connect) and the re-auth prompt everywhere. Modelled on
          OpenSea's "Authenticate to continue" sheet: a bottom sheet below 560px via PopupDialog,
          the centred card above it. Swiping down/backdrop only closes it (still connected);
          Cancel keeps its old meaning and disconnects. */}
      <PopupDialog
        open={signMessageDialogOpen}
        onClose={() => {
          setSignMessageDialogOpen(false);
          setError(false);
          setSigning(false);
        }}
        maxWidth={420}
      >
        <SignInSheet>
          <BrandDisc aria-hidden>
            <SvgIcon viewBox="0 0 26 29" sx={{ width: 32, height: 36, color: "white" }}>
              <path
                d="M13.0002 0.228L0.2 7.1582V21.3461L13.0002 28.6727L25.8 21.3461V7.1582L13.0002 0.228ZM23.6712 20.1048L14.0688 25.6127V22.027C13.612 22.1046 13.1466 22.1562 12.6639 22.1562C12.414 22.1562 12.1725 22.1391 11.9312 22.1132V25.6127L2.32881 20.1048V9.54575L5.55275 11.4508C5.82857 10.7957 6.19903 10.1837 6.65587 9.61477L3.50138 7.76153L12.9914 2.71045L22.4818 7.76153L20.3526 9.01134V11.4938L23.6539 9.53722V20.0963L23.6712 20.1048Z"
                fill="currentColor"
                stroke="currentColor"
                strokeWidth="0.39"
                strokeMiterlimit="10"
              />
              <path
                d="M8.88062 10.6567C7.83764 11.6997 7.312 12.9668 7.312 14.4494C7.312 15.001 7.39825 15.5354 7.57926 16.044C7.76027 16.5612 8.00169 17.0439 8.29475 17.4921L7.67396 18.1127C7.50156 18.2851 7.42395 18.4833 7.42395 18.7074C7.42395 18.9315 7.51018 19.1298 7.67396 19.3022C7.84635 19.4746 8.04455 19.5522 8.26866 19.5436C8.49277 19.535 8.69097 19.4574 8.86336 19.3022L9.45806 18.7419C9.94938 19.0609 10.4582 19.3195 10.9926 19.5177C11.527 19.716 12.087 19.8107 12.6732 19.8107C14.1557 19.8107 15.4232 19.285 16.4661 18.242C17.5091 17.1991 18.0347 15.9319 18.0347 14.4494V9.088H12.6732C11.1906 9.088 9.92359 9.61375 8.88062 10.6567ZM16.3365 14.4494C16.3365 15.4665 15.9833 16.3371 15.2679 17.0439C14.5525 17.7593 13.6903 18.1127 12.6732 18.1127C12.3198 18.1127 11.975 18.0524 11.6475 17.9317C11.3199 17.8111 10.9925 17.6731 10.6736 17.5265L13.8887 14.3114C14.0611 14.139 14.1387 13.9408 14.1387 13.7081C14.1387 13.4754 14.0524 13.2684 13.8887 13.1047C13.7163 12.9323 13.5181 12.8548 13.294 12.8548C13.0699 12.8548 12.8717 12.9409 12.6993 13.1047L9.45806 16.3456C9.32877 16.0267 9.21682 15.7078 9.13062 15.3975C9.04442 15.0872 9.00141 14.7683 9.00141 14.4494C9.00141 13.4323 9.3546 12.5617 10.07 11.8549C10.7855 11.1394 11.6476 10.786 12.6647 10.786H16.3281V14.4494H16.3365Z"
                fill="currentColor"
                stroke="currentColor"
                strokeWidth="0.39"
                strokeMiterlimit="10"
              />
            </SvgIcon>
          </BrandDisc>
          <SignInTitle>Authenticate to continue</SignInTitle>
          <SignInBody>Please sign in with your connected wallet to continue.</SignInBody>
          {error && (
            <SignInError role="alert">
              {errorMessage ? errorMessage : "We are experiencing some technical issues. Please try again later!"}
            </SignInError>
          )}
          {signatureLoading ? (
            <PopupProcessingButton disabled variant="contained" fullWidth>
              <CircularProgress size={20} sx={{ color: "white" }} />
            </PopupProcessingButton>
          ) : (
            <PopupActionButton onClick={handleSignMessage} variant="contained" fullWidth>
              Sign in
            </PopupActionButton>
          )}
          <PopupGhostButton onClick={handleDisconnect} disabled={signatureLoading} fullWidth>
            Cancel
          </PopupGhostButton>
          <SignInFootnote>
            EcoFutures uses this signature to verify that you&apos;re the owner of this wallet address.
          </SignInFootnote>
        </SignInSheet>
      </PopupDialog>

      <DialogInfo
        open={wrongChainDialogOpen}
        onClose={() => {
          setWrongChainDialogOpen(false);
          setError(false);
          setSigning(false);
        }}
      >
        <IconContainer>
          <CircularIcon>
            <InfoOutlinedIcon fontSize="large" color="inherit" />
          </CircularIcon>
        </IconContainer>
        <InfoBody>
          <br />
          <p style={{ fontWeight: 600, color: "black", fontSize: "x-large", marginBottom: "0px" }}>
            Unsupported network
          </p>
          <p style={{ lineHeight: "1.5", width: isSmallScreen ? "100%" : "85%" }}>
            EcoFutures uses chainId {MainChainID} to provide a safe tool for investment into green assets. Please switch
            your wallet to the correct network to continue.
          </p>
          <br />
          <Grid
            container
            direction={isSmallScreen ? "column" : "row"}
            alignItems="center"
            justifyContent={"space-around"}
            sx={{
              borderBottom: "1px solid GrayText",
              pb: 8,
              width: "100%",
              gap: "20px",
            }}
          >
            <InfoButton
              onClick={handleDisconnect}
              sx={{
                width: "100%",
                maxWidth: "300px",
                backgroundColor: "black",
                border: "none",
                textTransform: "none",
                ":hover": {
                  border: "none",
                  backgroundColor: "#333",
                  color: "white",
                },
              }}
            >
              Disconnect
            </InfoButton>
          </Grid>

          <p style={{ fontSize: "12px", color: "GrayText", marginTop: "16px" }}>
            Switch your wallet's network to chain {MainChainID} and reconnect to continue.
          </p>
        </InfoBody>
      </DialogInfo>
    </Wrapper>
  );
};

const Wrapper = styled("div")`
  display: flex;
  align-items: center;
  gap: 24px;

  // @media (max-width: 560px) {
  //   width: 100%;
  //   padding: 24px;
  //   border: 1px solid ${({ theme }) => theme.palette.divider};
  // }
`;

const Button = styled(MuiButton)`
  height: 54px;
  position: relative;

  &.Mui-disabled {
    img {
      opacity: 0.5;
    }
  }
  @media (max-width: 992px) {
    width: -webkit-fill-available;
    height: 54px;
  }
`;

const InfoButton = styled(MuiButton)`
  height: 54px;
  margin-bottom: 10px;
  position: relative;
  border: none;

  &:hover {
    border: none;
    background-color: #333;
    color: white;
  }

  &.Mui-disabled {
    img {
      opacity: 0.5;
    }
  }
  @media (max-width: 992px) {
    width: inherit;
    height: 54px;
  }
`;

// The EFT mark (same artwork as assets/icons/eft_icon.svg and the EcoBank header icon), white
// on a black disc — where OpenSea puts its logo on this sheet.
const BrandDisc = styled("div")({
  width: 72,
  height: 72,
  marginBottom: 12,
  borderRadius: "50%",
  backgroundColor: "black",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

// The popup CTA pill, a little taller so the MetaMask fox (WalletIcon, absolutely positioned on
// the right) sits inside it with room to spare.
const WalletConnectButton = styled(PopupActionButton)({
  position: "relative",
  height: 48,
  marginTop: 8,
  // PopupButtons' phone rule (`&&`, height auto / minHeight 40) would squeeze the fox; this
  // doubled class is injected later, so it wins at the same specificity.
  "&&": { "@media (max-width: 560px)": { minHeight: 48 } },
  "&.Mui-disabled img": { opacity: 0.5 },
});

const SignInError = styled("p")({
  margin: "0 0 8px",
  color: "#B00020",
  fontFamily: "'Inter', sans-serif",
  fontSize: 13,
  lineHeight: 1.4,
});

const SignInFootnote = styled("p")({
  margin: "8px 0 0",
  color: "GrayText",
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  lineHeight: 1.4,
  "& a": { color: "inherit", textUnderlineOffset: 2 },
});

// const Progress = styled(CircularProgress)`
//   position: absolute;
//   left: 100px;
//  @media (max-width: 560px) {
//       left: 140px;
//     }
//   svg {
//     width: 20px;
//     height: 20px;
//   }
// `;

export const ProfileButton = styled(IconButton)`
  border: none;

  background: var(--purple);
  &:hover {
    background: var(--purple);
    opacity: 0.8;
  }
`;

const WalletIcon = styled("img")`
  position: absolute;
  right: 15px;
  width: 30px;
`;

export const VerticalDivider = styled("div")(({ theme }) => ({
  height: "20px",
  width: "1px",
  background: theme.palette.common.black,
  "@media(max-width: 992px)": {
    display: "none",
  },
}));

const Link = styled(RouterDom.Link)`
  cursor: pointer;
  font-size: 16px;

  @media (max-width: 992px) {
    font-size: 14px;
  }

  font-weight: normal;
  text-align: center;
  text-decoration: none;

  &:hover {
    opacity: 0.8;
  }

  @media (max-width: 560px) {
    width: 100%;
    //border: 1px solid ${({ theme }) => theme.palette.divider};
    &:not(:last-child) {
      border-bottom: none;
    }
  }
`;

const LinkLike = styled("div")`
  cursor: pointer;
  font-weight: normal;
  text-align: center;
  text-decoration: none;
  @media (max-width: 560px) {
    width: 100%;
    //border: 1px solid ${({ theme }) => theme.palette.divider};
    &:not(:last-child) {
      border-bottom: none;
    }
  }
`;

const InfoBody = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: center;
  padding: 40px 40px 24px 40px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 560px) {
    padding: 30px 20px;
    overflow-y: visible;
  }

  p {
    font-size: 16px;
    font-family: Inter, sans-serif;
    align-self: center;
  }

  a {
    color: black;
    text-underline-offset: 2px;
    font-size: 16px;
    font-family: Inter, sans-serif;
  }
`;

export const GuardianIcon = createSvgIcon(
  // eslint-disable-next-line max-len
  <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#000000">
    <path d="M480-320q102-92 131-129.5t29-74.5q0-36-26-62t-62-26q-21 0-40.5 8.5T480-580q-12-15-31-23.5t-41-8.5q-36 0-62 26t-26 62q0 19 5 35t22 37.5q17 21.5 48.5 52.5t84.5 79Zm0 240q-139-35-229.5-159.5T160-516v-244l320-120 320 120v244q0 152-90.5 276.5T480-80Z" />
  </svg>,
  "Guardian"
);

// Pioneer and Global Trust Admin are the Figma role glyphs (32 × 32 frame, see
// components/RoleIcons). The square viewBox is centred on the shield and sized so it fills the
// box the way the Material shields above do (5/6 of the height).
export const PioneerIcon = createSvgIcon(
  // eslint-disable-next-line max-len
  <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="4.878 4.913 22.164 22.164" width="24px" fill="#000000">
    <path
      fillRule="evenodd"
      d="M15.96 25.23C13.8 24.7 12.0098 23.48 10.5898 21.56C9.16984 19.64 8.45996 17.51 8.45996 15.16V9.65002L15.96 6.76001L23.46 9.65002V15.16C23.46 17.5 22.7501 19.64 21.3301 21.56C19.9101 23.48 18.12 24.71 15.96 25.23ZM13.25 19.39H14.71V15.88H19.8301L18.1899 13.68L19.8301 11.49H13.25V19.39Z"
    />
  </svg>,
  "Pioneer"
);

export const GlobalTrustAdminIcon = createSvgIcon(
  // eslint-disable-next-line max-len
  <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="5.566 5.541 20.868 20.868" width="24px" fill="#000000">
    <path d="M16 24.67C13.97 24.16 12.3 22.99 11 21.14C9.7 19.29 9.04004 17.27 9.04004 15.06V9.89001L16 7.28003L22.96 9.89001V15.06C22.96 17.26 22.31 19.29 21 21.14C19.69 22.99 18.03 24.17 16 24.67ZM16 23.32C17.54 22.81 18.8101 21.88 19.8101 20.53C20.8201 19.18 21.4101 17.66 21.5801 15.98H15.9902V8.70001L10.3403 10.81V15.07C10.3403 15.24 10.3401 15.39 10.3501 15.52C10.3601 15.65 10.3804 15.8001 10.4004 15.9901H15.9902V23.34L16 23.32Z" />
  </svg>,
  "Global Trust Admin"
);

export const TrustAdminIcon = createSvgIcon(
  // eslint-disable-next-line max-len
  <svg xmlns="http://www.w3.org/2000/svg" height="48px" viewBox="0 -960 960 960" width="48px" fill="#000000">
    <path d="m374-353 106-78 103 78-43-126 106-76H518l-38-125-38 125H314l104 76-44 126ZM480-81q-140-35-230-162.5T160-523v-238l320-120 320 120v238q0 152-90 279.5T480-81Zm0-62q115-38 187.5-143.5T740-523v-196l-260-98-260 98v196q0 131 72.5 236.5T480-143Zm0-337Z" />
  </svg>,
  "Trust Admin"
);
